import { ChatFieldValueKind, ChatProposalKind } from '@insio/shared-types';

/** Values of one kind a change row shows translated. */
export interface ChatFieldValues {
  kind: ChatFieldValueKind;
  values: string[];
}

const VALUE_KINDS: ReadonlySet<string> = new Set(Object.values(ChatFieldValueKind));

/** Reads untrusted typed row values; null when they are not a known kind with text values. */
function readValues(value: unknown): ChatFieldValues | null {
  if (!isRecord(value) || typeof value.kind !== 'string' || !VALUE_KINDS.has(value.kind)) {
    return null;
  }
  if (!Array.isArray(value.values)) return null;
  const values = value.values.filter((entry): entry is string => typeof entry === 'string');
  // Safe: membership in VALUE_KINDS was checked above.
  return values.length === 0 ? null : { kind: value.kind as ChatFieldValueKind, values };
}

/** One record a command card acts on. */
export interface ChatCommandItem {
  id: string;
  number: string;
}

/** A command card payload: one action applied to every listed record. */
export interface ChatCommandPayload {
  command: string;
  note: string | null;
  items: ChatCommandItem[];
}

/** One field row of a change card. */
export interface ChatFieldChangeRow {
  field: string;
  from: string | null;
  to: string | null;
  /** A plugin setting's own label per language; null on a row that is not a plugin setting. */
  label: Record<string, string> | null;
  /** Typed values shown translated, joined, in place of `to`; null when the row has none. */
  toValues: ChatFieldValues | null;
  /** A plugin enum option's own label per language; empty when none. */
  toLabel: Record<string, string>;
  /** Typed values shown translated in place of `from`; null when the row has none. */
  fromValues: ChatFieldValues | null;
  /** A plugin enum option's own label for `from`; empty when none. */
  fromLabel: Record<string, string>;
}

/** A change card payload: field changes to one record, shown before → after. */
export interface ChatChangePayload {
  target: string;
  /** Key under `proposals.chat.targets`; `target` is shown when it has no translation. */
  targetKey: string | null;
  targetParams: Record<string, string | number>;
  changes: ChatFieldChangeRow[];
}

function readParams(value: unknown): Record<string, string | number> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string | number] =>
        typeof entry[1] === 'string' || typeof entry[1] === 'number'
    )
  );
}

/** The string entries of an untrusted per-language text. */
function readTexts(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const textOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/** Reads an untrusted command payload; null when it is not one. */
export function readChatCommand(payload: unknown): ChatCommandPayload | null {
  if (!isRecord(payload) || payload.kind !== ChatProposalKind.COMMAND) return null;
  if (typeof payload.command !== 'string' || !Array.isArray(payload.items)) return null;
  const items = payload.items.flatMap(entry =>
    isRecord(entry) && typeof entry.id === 'string' && typeof entry.number === 'string'
      ? [{ id: entry.id, number: entry.number }]
      : []
  );
  return items.length > 0
    ? { command: payload.command, note: textOrNull(payload.note), items }
    : null;
}

/** One secret a card's form asks for; its value is typed in, never part of the payload. */
export interface ChatSecretField {
  key: string;
  required: boolean;
  /** The plugin's own label per UI language; empty when it declares none. */
  label: Record<string, string>;
}

/** The secret fields a change card's form asks for; empty when it asks for none. */
export function readChatSecretFields(payload: unknown): ChatSecretField[] {
  if (!isRecord(payload) || !isRecord(payload.input)) return [];
  const fields = payload.input.secretFields;
  if (!Array.isArray(fields)) return [];
  return fields.flatMap(entry =>
    isRecord(entry) && typeof entry.key === 'string' && entry.key !== ''
      ? [{ key: entry.key, required: entry.required === true, label: readTexts(entry.label) }]
      : []
  );
}

/** Reads an untrusted change payload; null when it is not one. */
export function readChatChange(payload: unknown): ChatChangePayload | null {
  if (!isRecord(payload) || payload.kind !== ChatProposalKind.CHANGE) return null;
  if (typeof payload.target !== 'string' || !Array.isArray(payload.changes)) return null;
  const changes = payload.changes.flatMap(entry =>
    isRecord(entry) && typeof entry.field === 'string'
      ? [
          {
            field: entry.field,
            from: textOrNull(entry.from),
            to: textOrNull(entry.to),
            label: isRecord(entry.label) ? readTexts(entry.label) : null,
            toValues: readValues(entry.toValues),
            toLabel: readTexts(entry.toLabel),
            fromValues: readValues(entry.fromValues),
            fromLabel: readTexts(entry.fromLabel)
          }
        ]
      : []
  );
  return changes.length > 0
    ? {
        target: payload.target,
        targetKey: textOrNull(payload.targetKey),
        targetParams: readParams(payload.targetParams),
        changes
      }
    : null;
}
